import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { propertyAPI, guestAPI } from '@/services/api';
import { Users, Mail, Phone, CreditCard } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

export default function GuestsPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchGuests();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      if (response.data.length > 0) {
        
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await guestAPI.getAll(selectedProperty);
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
      toast.error('Error al cargar huéspedes');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando huéspedes...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="guests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="guests-title">
              Huéspedes
            </h1>
            <p className="text-muted-foreground mt-1">
              Cartera de clientes de tu propiedad
            </p>
          </div>

          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-full sm:w-[280px]" data-testid="guests-property-selector">
                <SelectValue placeholder="Selecciona una propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Guests List */}
        <div className="space-y-4">
          {guests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2" style={{fontFamily: 'Manrope'}}>
                  No hay huéspedes registrados
                </h3>
                <p className="text-muted-foreground">
                  Los huéspedes aparecerán aquí cuando tengas reservas
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {guests.map((guest, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow duration-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span style={{fontFamily: 'Manrope'}}>{guest.name}</span>
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{guest.email}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{guest.phone}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground font-mono">{guest.documento}</span>
                    </div>

                    <div className="pt-3 border-t border-border">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-muted-foreground">Reservas</p>
                          <p className="text-lg font-bold" style={{fontFamily: 'Manrope'}}>
                            {guest.total_reservas}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total Gastado</p>
                          <p className="text-lg font-bold" style={{fontFamily: 'Manrope'}}>
                            ${guest.total_gastado.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Última Visita</p>
                          <p className="text-lg font-bold" style={{fontFamily: 'Manrope'}}>
                            {format(parseISO(guest.ultima_visita), 'dd MMM', { locale: es })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {guests.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle style={{fontFamily: 'Manrope'}}>Estadísticas de Clientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clientes</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    {guests.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reservas Totales</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    {guests.reduce((sum, g) => sum + g.total_reservas, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ingresos Totales</p>
                  <p className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
                    ${guests.reduce((sum, g) => sum + g.total_gastado, 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
